import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { MenuContext } from '../context/MenuContext';

import { useDebounce } from '../hooks/useDebounce';

import { getMenuCategoriesRequest } from '../api/category/category.action';
import { getCustomFieldsRequest } from '../api/custom-field/custom-field.action';
import { getMenusRequest } from '../api/menu/menu.action';
import { getPromotionsRequest } from '../api/promotion/promotion.action';
import { trackEvent } from '../utils/analytics/metaPixel';

export const LAYOUT_GRID = 'grid';
export const LAYOUT_LIST = 'list';

export const SORT_POPULAR = 'popular';
export const SORT_PRICE_ASC = 'price-asc';
export const SORT_PRICE_DESC = 'price-desc';
export const SORT_NAME_ASC = 'name-asc';

const sortMenuItems = (items, sortBy) => {
  if (sortBy === SORT_PRICE_ASC) return [...items].sort((a, b) => a.price - b.price);
  if (sortBy === SORT_PRICE_DESC) return [...items].sort((a, b) => b.price - a.price);
  if (sortBy === SORT_NAME_ASC) return [...items].sort((a, b) => a.name.localeCompare(b.name));
  return items;
};

export const MenuProvider = ({ children }) => {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 100);
  const [menuLayout, setMenuLayout] = useState(LAYOUT_GRID);

  const dispatch = useDispatch();
  const { menus, loading: menusLoading } = useSelector((state) => state.menu);
  const { categories, loading: categoriesLoading } = useSelector((state) => state.categories);
  const { promotions, loading: promotionsLoading } = useSelector((state) => state.promotions);
  const { customFields, loading: customFieldsLoading } = useSelector((state) => state.customFields);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sortBy, setSortBy] = useState(SORT_POPULAR);

  const getUnfilteredCategory = (categories) =>
    Array.isArray(categories)
      ? categories.find((category) => !category.filterEnabled)?.id
      : undefined;

  useEffect(() => {
    dispatch(getMenusRequest());
    dispatch(
      getMenuCategoriesRequest((categories) => {
        setSelectedCategory(getUnfilteredCategory(categories));
      })
    );
    dispatch(getPromotionsRequest());
    dispatch(getCustomFieldsRequest());
  }, [dispatch]);

  // Only report a search once the shopper stops typing, not on every keystroke
  const searchToTrack = useDebounce(query, 1500);
  useEffect(() => {
    const searchString = searchToTrack?.trim();
    if (searchString?.length >= 2) {
      trackEvent('Search', { search_string: searchString });
    }
  }, [searchToTrack]);

  const handleSearch = (e) => {
    setQuery(e?.value);
  };

  const memoizedMenus = useMemo(() => {
    let searchedMenus = [...menus];

    if (debouncedQuery) {
      searchedMenus = menus.reduce((list, menu) => {
        const filteredItems = menu.menuItems.filter(
          (item) =>
            item.name.toLowerCase().includes(debouncedQuery.toLowerCase()) ||
            item.description.toLowerCase().includes(debouncedQuery.toLowerCase())
        );

        if (filteredItems.length > 0) {
          list.push({ ...menu, menuItems: filteredItems });
        }

        return list;
      }, []);
    }

    if (selectedCategory && selectedCategory !== getUnfilteredCategory(categories)) {
      searchedMenus = searchedMenus.reduce((list, menu) => {
        const filteredItems = menu.menuItems.filter((item) => {
          return item.categories.some((category) => category.id === selectedCategory);
        });

        if (filteredItems.length > 0) {
          list.push({ ...menu, menuItems: filteredItems });
        }

        return list;
      }, []);
    }

    return searchedMenus.map((menu) => ({
      ...menu,
      menuItems: sortMenuItems(menu.menuItems, sortBy)
    }));
  }, [debouncedQuery, menus, selectedCategory, categories, sortBy]);

  return (
    <MenuContext.Provider
      value={{
        customFields,
        promotions,
        categories,
        menus: memoizedMenus,
        selectedCategory,
        setSelectedCategory,
        sortBy,
        setSortBy,
        handleSearch,
        query,
        menuLayout,
        setMenuLayout,
        menusLoading,
        categoriesLoading,
        promotionsLoading,
        customFieldsLoading
      }}
    >
      {children}
    </MenuContext.Provider>
  );
};
